import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { TRY_THEN_BUY_FRAUD } from '../../../constants/define/translation';
import { ConfirmAction } from '../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../repository/context/userContext';
import { MESSAGE_ERROR } from '../../../constants/message';
import { postLoginRepository } from '../../../repository/mytm/auth/loginRepository';
import { ModalThresholdInput } from './modals/ModalThresholdInput';
import {
  ThresholdResponse,
  getTTBFraudConfigThresholdListRepository,
  postConfigThresholdRepository,
} from '../../../repository/mytm/try-then-buy-fraud/ttbFraudRepository';
import { API_URLS } from '../../../constants/urls/api-urls';

type TypeLabel = 'hourly' | 'daily' | 'weekly' | 'monthly';

interface ThresholdType {
  label: TypeLabel;
  value: string;
}

const thresholdsDefault: ThresholdType[] = [
  { label: 'hourly', value: '' },
  { label: 'daily', value: '' },
  { label: 'weekly', value: '' },
  { label: 'monthly', value: '' },
];

export const TTBFraudThreshold = () => {
  const { t } = useTranslation(TRY_THEN_BUY_FRAUD);
  const userContext = useContext(UserContext);
  const [isOpenModalThreshold, setIsOpenModalThreshold] = useState(false);
  const [thresholds, setThresholds] = useState<ThresholdType[]>(thresholdsDefault);
  const [threshold, setThreshold] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  useEffect(() => {
    getTTBFraudConfigThresholdListRepository(API_URLS.CONFIG_THRESHOLD)
      .then((res: ThresholdResponse) => {
        const mapData = thresholds.map((item: ThresholdType) => {
          const foundItem = (res as any).find((obj: ThresholdResponse) => obj?.type?.toLowerCase() === item.label);
          return foundItem
            ? {
                label: foundItem.type.toLowerCase(),
                value: foundItem.totalOrder,
              }
            : item;
        });
        setThresholds(mapData);
      })
      .catch(e => {});
  }, [refresh]);

  const onOpenThresholdModal = useCallback((key: TypeLabel, modalContent: string) => {
    setThreshold(key);
    setTitle(t(`setting.popup.${key}`));
    setContent(modalContent);
    setIsOpenModalThreshold(true);
  }, []);

  const onCloseThresholdModal = useCallback(() => {
    setIsOpenModalThreshold(false);
  }, []);

  const handleSubmitThreshold = async (value: any) => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('setting.popup.confirm')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        onCloseThresholdModal();
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postConfigThresholdRepository(API_URLS.CONFIG_THRESHOLD, {
            name: threshold,
            type: threshold.toUpperCase(),
            totalOrder: Number(value.content),
          })
            .then(() => {
              setRefresh(!refresh);
              message.success(t('message.updateSuccess'));
            })
            .catch(e => {
              message.error(MESSAGE_ERROR.M02);
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          setLoading(true);
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              message.error(MESSAGE_ERROR.M02);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      },
    });
  };

  return (
    <StyledRowSsetting>
      <Col span={6} className="full-width">
        <span className="label">{t('setting.title')}: </span>
      </Col>
      {thresholds.map(item => (
        <Col span={4} className="full-width" key={item.label}>
          <span className="label">{t(`setting.${item.label}`)}:</span>
          <span className="value">{item.value}</span>
          <Tooltip title={t('form.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              loading={threshold === item.label ? loading : false}
              onClick={() => onOpenThresholdModal(`${item.label}`, item.value)}
            />
          </Tooltip>
        </Col>
      ))}
      {isOpenModalThreshold && (
        <ModalThresholdInput
          onSubmit={handleSubmitThreshold}
          isOpenModal={isOpenModalThreshold}
          onClose={onCloseThresholdModal}
          title={title}
          content={content}
          placeholder={t('form.placeholderEnter')}
        />
      )}
    </StyledRowSsetting>
  );
};

const StyledRowSsetting = styled(Row)`
  padding: 0.5rem 0 0.75rem;
  & .full-width {
    .ant-select {
      width: 280px;
      min-width: 280px;
      max-width: 400px;
    }
  }

  & .ant-col {
    display: flex;
    align-items: center;
    & .label,
    .value {
      font-weight: bold;
      font-size: 14px;
      display: table-cell;
      vertical-align: middle;
    }
    & .value {
      padding-left: 5px;
    }
  }
`;
